import { useEffect, useState, useRef } from "react";
import { postData } from "../../ApiServices/ApiService";
import { ORG_EXPENSE_TYPE_CRUD, ORG_EXPENSE_TYPE_LIST } from "../../ApiServices/BaseURL";
import { logFrontendException } from "../../Utils/Exception";
import { useTheme } from "../../GlobalContext/GlobalContext";

const useNatureExpenseController = () => {
    const [natureTypes, setNatureTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});
    const [deleteError, setDeleteError] = useState({});
    const [deleteSuccess, setDeleteSuccess] = useState({});
    const [error, setError] = useState("");
    const { setIsLoginPopup } = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const natureInfoRef = useRef(null);
    const pageName = 'useNatureExpenseController';
    const storedUserId = JSON.parse(localStorage.getItem("_userInfo_"))?.emp_id;

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;



    useEffect(() => {
        fetchNatureTypes();
    }, []);

    const fetchNatureTypes = async () => {
        setLoading(true);
        setError("");
        setNatureTypes([]);
        try {
            const storedUserInfo = JSON.parse(localStorage.getItem("_userInfo_"));
            const payload = {
                emp_id_auth: storedUserInfo?.emp_id
            };

            const response = await postData(ORG_EXPENSE_TYPE_LIST, payload);
            const responseData = await response.json();

            if (response.status === 200 && responseData.status_code === 200) {
                if (responseData.message.toLowerCase() === "success" && responseData?.dataJ?.length > 0) {
                    setNatureTypes(responseData.dataJ || []);
                } else {
                    setError(responseData.info || "Failed to fetch expense types.");
                    //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
                    logFrontendException(storedUserId, pageName, "fetchNatureTypes", responseData || '');
                }
            }else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                
                setError("Unauthorized access. Your session may have expired. Please log in again.");
                setIsLoginPopup(true)
                //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
                logFrontendException(storedUserId, pageName, "fetchNatureTypes", responseData || response || '');
                       
                } else {
                setError(responseData.info || "Failed to load expense types.");
                //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
                logFrontendException(storedUserId, pageName, "fetchNatureTypes", responseData || '');

            }
        } catch (err) {
            setError("Network error. Please try again.");
            //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
            logFrontendException(storedUserId, pageName, "fetchNatureTypes", err || '');

        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        natureInfoRef.current = null;
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        natureInfoRef.current = item;
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleDelete = (item) => {
        natureInfoRef.current = item;
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const confirmDelete = async (item) => {
        const id = item.id;
        setDeleteLoading((prev) => ({ ...prev, [id]: true }));
        setDeleteError((prev) => ({ ...prev, [id]: "" }));
        setDeleteSuccess((prev) => ({ ...prev, [id]: "" }));

        try {
            const storedUserInfo = JSON.parse(localStorage.getItem("_userInfo_"));
            const payload = {
                emp_id_auth: storedUserInfo?.emp_id,
                expense_type_id: id,
                action_code: "DELETE_EXPENSE_TYPE",
            };

            const response = await postData(ORG_EXPENSE_TYPE_CRUD, payload);
            const responseData = await response.json();

            if (response.status === 200) {
                if(responseData?.status_code === 200) {
                    if(responseData.message.toLowerCase() === "success"){
                        setDeleteSuccess((prev) => ({ ...prev, [id]: responseData.info }));
                        setTimeout(() => {
                        setDeleteSuccess((prev) => ({ ...prev, [id]: "" }));
                        fetchNatureTypes(); // refresh list
                        }, 2000);
                    } else{
                        setDeleteError((prev) => ({ ...prev, [id]: responseData.info || "Failed to delete." }));
                        // Log exception to backend (User Id,Page Name,Method Name,Response/error)
                        logFrontendException(storedUserId, pageName, "confirmDelete", responseData?.info || '');
                    }
                } else {
                    setDeleteError((prev) => ({ ...prev, [id]: responseData.info || "Failed to delete.Please try again." }));
                    // Log exception to backend (User Id,Page Name,Method Name,Response/error)
                    logFrontendException(storedUserId, pageName, "confirmDelete", responseData || '');
                }
            } else if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                setDeleteError('Unauthorized access. Your session may have expired. Please log in again.');
                setIsLoginPopup(true);
                logFrontendException(storedUserId, pageName, "confirmDelete", responseData || '');
            } else {
                setDeleteError((prev) => ({ ...prev, [id]: responseData.info || "Failed to delete." }));
                //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
                logFrontendException(storedUserId, pageName, "confirmDelete", responseData || response || '');
            }
        } catch (err) {
            setDeleteError((prev) => ({ ...prev, [id]: "Failed to delete.Please check your internet connection and try again." }));
            //  Log exception to backend (User Id,Page Name,Method Name,Response/error)
            logFrontendException(storedUserId, pageName, "confirmDelete-catch", err || '');
        } finally {
            setDeleteLoading((prev) => ({ ...prev, [id]: false }));
        }
    };

    const onValueChange = () => {
        fetchNatureTypes();
    };

    // Pagination logic
    const totalPages = Math.ceil(natureTypes.length / itemsPerPage);
    const currentNatureTypes = natureTypes.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const visiblePages = Array.from(
        { length: totalPages },
        (_, i) => i + 1
    ).slice(
        Math.max(0, currentPage - 2),
        Math.min(totalPages, currentPage + 1)
    );

    const getStatusStyle = (status) => {
        switch (status) {
            case 1:
                return { backgroundColor: "#1A932E2E", color: "#1A932E" };
            case 3:
                return { backgroundColor: "#FFF5D6", color: "#FFA800" };
            case 0:
                return { backgroundColor: "#FFE0E0", color: "#FF0000" };
            default:
                return {};
        }
    };


    return {
        natureTypes,
        currentNatureTypes,
        loading,
        error,
        isModalOpen,
        isDeleteModalOpen,
        handleAddNew,
        handleEdit,
        handleCloseModal,
        handleDelete,
        handleCloseDeleteModal,
        confirmDelete,
        deleteLoading,
        deleteError,
        deleteSuccess,
        natureInfoRef,
        onValueChange,
        currentPage,
        totalPages,
        handlePageChange,
        visiblePages,
        getStatusStyle
    };
};

export default useNatureExpenseController;
